import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import styles from "./PaymentPage.module.css";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import QRCode from "qrcode";

/* =========================================================
   API CONFIG
========================================================= */

const API_BASE_URL = "https://coinyoco.world/brobizee-api";

const API_CLEAR_SENDER_URL =
  `${API_BASE_URL}/clear_sender_details2.php`;

const API_GET_SENDER_URL =
  `${API_BASE_URL}/get_sender_details.php`;

/* ⭐ NEW API — fetch partnerdata.money from DB */
const API_GET_PARTNER_URL =
  `${API_BASE_URL}/get_partner_money.php`;

/* =========================================================
   ⭐ UPI PAYMENT CONFIG
========================================================= */

const PARTNER_UPI_ID = "ap080298@ibl";
const PARTNER_PAYEE_NAME = "BroBizee Partner";
const PAYMENT_NOTE = "Payment via BroBizee";

/* =========================================================
   HELPERS
========================================================= */

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value).trim() || fallback;
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const EMPTY_SENDER = {
  sender_profile_images: "",
  sender_name: "",
  sender_number: "",
  sender_money: "",
  sender_address: "",
  sender_lat_long: "",
  sender_chatbox: "",
  sender_request: "",
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function PaymentPage() {
  const navigate = useNavigate();
  const params = useParams();

  /* ⭐ URL params — DB identifiers
     (Moved ABOVE handleClick to fix TDZ — old logic intact) */
  const userId =
    params?.userId || params?.userIdA || "";

  const uuid =
    params?.uuid || params?.deviceUuid || "";

  /* ⭐ handleClick — go to show_payment (kept same but now safe) */
  const handleClick = () => {
    navigate(`/${userId}/${uuid}/show_payment`);
  };

  /* =========================================================
     STATE
  ========================================================= */

  const [paymentMethod, setPaymentMethod] =
    useState("cash");

  const [loading, setLoading] = useState(false);

  const [senderDetails, setSenderDetails] =
    useState(EMPTY_SENDER);

  const [senderLoading, setSenderLoading] =
    useState(true);

  const [showMotivationPopup, setShowMotivationPopup] =
    useState(false);

  /* ⭐ QR CODE states */
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");

  /* =========================================================
     ⭐ NEW: PARTNER MONEY from partnerdata table
     (This makes money visible on BACK navigation too)
  ========================================================= */

  const [partnerMoney, setPartnerMoney] = useState(null);

  /* =========================================================
     SENDER DATA (localStorage fallback for amount card)
     ⭐ KEPT SAME as original
  ========================================================= */

  const senderData = useMemo(() => {
    try {
      const name =
        localStorage.getItem(
          "selected_sender_name"
        ) || "";

      const money = safeNumber(
        localStorage.getItem(
          "selected_sender_money"
        ),
        0
      );

      const request =
        localStorage.getItem(
          "selected_sender_request"
        ) || "";

      return {
        name: safeString(name, "Customer"),
        money,
        request: safeString(request, ""),
      };
    } catch {
      return {
        name: "Customer",
        money: 0,
        request: "",
      };
    }
  }, []);

  /* =========================================================
     ⭐ NEW EFFECT — FETCH PARTNER MONEY FROM DB
     Runs on mount & whenever userId/uuid change.
     This ensures when user presses BACK, money is restored.
  ========================================================= */

  useEffect(() => {
    let cancelled = false;

    const loadPartnerMoney = async () => {
      /* No params → try localStorage only */
      if (!userId && !uuid) {
        try {
          const localMoney = localStorage.getItem(
            "selected_sender_money"
          );
          if (localMoney !== null && !cancelled) {
            setPartnerMoney(localMoney);
          }
        } catch {}
        return;
      }

      try {
        const formData = new FormData();
        formData.append("secondary_id", String(userId));
        formData.append("secondary_uuid", String(uuid));

        console.log("PARTNER MONEY → sending:", {
          secondary_id: userId,
          secondary_uuid: uuid,
        });

        const response = await fetch(API_GET_PARTNER_URL, {
          method: "POST",
          body: formData,
        });

        const result = await response.json();

        console.log("PARTNER MONEY RESULT:", result);

        if (!cancelled && result?.success && result?.data) {
          const money = result.data.money;

          if (money !== null && money !== undefined) {
            setPartnerMoney(String(money));

            /* ⭐ Sync to localStorage so future reads work */
            try {
              localStorage.setItem(
                "selected_sender_money",
                String(money)
              );

              if (result.data.full_name) {
                localStorage.setItem(
                  "selected_sender_name",
                  result.data.full_name
                );
              }

              if (result.data.phone_number) {
                localStorage.setItem(
                  "selected_sender_number",
                  result.data.phone_number
                );
              }

              if (result.data.profile) {
                localStorage.setItem(
                  "selected_sender_profile",
                  result.data.profile
                );
              }
            } catch {}
          }
        } else {
          /* Fallback to localStorage */
          try {
            const localMoney = localStorage.getItem(
              "selected_sender_money"
            );
            if (localMoney !== null && !cancelled) {
              setPartnerMoney(localMoney);
            }
          } catch {}
        }
      } catch (err) {
        console.error("Partner money fetch error:", err);

        /* Fallback to localStorage on error */
        try {
          const localMoney = localStorage.getItem(
            "selected_sender_money"
          );
          if (localMoney !== null && !cancelled) {
            setPartnerMoney(localMoney);
          }
        } catch {}
      }
    };

    loadPartnerMoney();

    return () => {
      cancelled = true;
    };
  }, [userId, uuid]);

  /* =========================================================
     LOAD SENDER DETAILS FROM DB ON MOUNT
     ⭐ KEPT SAME — nothing removed
  ========================================================= */

  useEffect(() => {
    let cancelled = false;

    const loadSenderDetails = async () => {
      setSenderLoading(true);

      /* If no URL params at all → fallback to localStorage */
      if (!userId && !uuid) {
        const fallback = {
          sender_profile_images:
            localStorage.getItem(
              "selected_sender_profile"
            ) || "",
          sender_name:
            localStorage.getItem(
              "selected_sender_name"
            ) || "",
          sender_number:
            localStorage.getItem(
              "selected_sender_number"
            ) || "",
          sender_money:
            localStorage.getItem(
              "selected_sender_money"
            ) || "",
          sender_address:
            localStorage.getItem(
              "selected_sender_address"
            ) || "",
          sender_lat_long:
            localStorage.getItem(
              "selected_sender_lat_long"
            ) || "",
          sender_chatbox:
            localStorage.getItem(
              "selected_sender_chatbox"
            ) || "",
          sender_request:
            localStorage.getItem(
              "selected_sender_request"
            ) || "",
        };

        if (!cancelled) {
          setSenderDetails(fallback);
          setSenderLoading(false);
        }
        return;
      }

      try {
        const formData = new FormData();
        formData.append(
          "secondary_id",
          String(userId)
        );
        formData.append(
          "secondary_uuid",
          String(uuid)
        );

        console.log("LOAD SENDER → sending:", {
          secondary_id: userId,
          secondary_uuid: uuid,
        });

        const response = await fetch(
          API_GET_SENDER_URL,
          {
            method: "POST",
            body: formData,
          }
        );

        const result = await response.json();

        console.log("LOAD SENDER RESULT:", result);

        if (
          !cancelled &&
          result?.success &&
          result?.data
        ) {
          const d = result.data;

          setSenderDetails({
            sender_profile_images:
              safeString(d.sender_profile_images),
            sender_name:
              safeString(d.sender_name),
            sender_number:
              safeString(d.sender_number),
            sender_money:
              d.sender_money !== null &&
              d.sender_money !== undefined
                ? String(d.sender_money)
                : "",
            sender_address:
              safeString(d.sender_address),
            sender_lat_long:
              safeString(d.sender_lat_long),
            sender_chatbox:
              safeString(d.sender_chatbox),
            sender_request:
              safeString(d.sender_request),
          });
        } else if (!cancelled) {
          /* Fallback to localStorage if API fails */
          setSenderDetails({
            sender_profile_images:
              localStorage.getItem(
                "selected_sender_profile"
              ) || "",
            sender_name:
              localStorage.getItem(
                "selected_sender_name"
              ) || "",
            sender_number:
              localStorage.getItem(
                "selected_sender_number"
              ) || "",
            sender_money:
              localStorage.getItem(
                "selected_sender_money"
              ) || "",
            sender_address:
              localStorage.getItem(
                "selected_sender_address"
              ) || "",
            sender_lat_long:
              localStorage.getItem(
                "selected_sender_lat_long"
              ) || "",
            sender_chatbox:
              localStorage.getItem(
                "selected_sender_chatbox"
              ) || "",
            sender_request:
              localStorage.getItem(
                "selected_sender_request"
              ) || "",
          });
        }
      } catch (err) {
        console.error(
          "Load sender details error:",
          err
        );
      } finally {
        if (!cancelled) {
          setSenderLoading(false);
        }
      }
    };

    loadSenderDetails();

    return () => {
      cancelled = true;
    };
  }, [userId, uuid]);

  /* =========================================================
     ⭐ BUILD UPI PAYMENT STRING
     Format: upi://pay?pa=UPI_ID&pn=NAME&am=AMOUNT&cu=INR&tn=NOTE
     ⭐ KEPT SAME
  ========================================================= */

  const buildUpiPaymentString = useCallback(() => {
    const amount = Number(senderData.money) || 0;

    const params = new URLSearchParams();
    params.append("pa", PARTNER_UPI_ID);
    params.append("pn", PARTNER_PAYEE_NAME);
    params.append("cu", "INR");

    /* Only add amount if > 0 */
    if (amount > 0) {
      params.append("am", amount.toFixed(2));
    }

    params.append(
      "tn",
      `${PAYMENT_NOTE} - ${senderData.name || "Customer"}`
    );

    return `upi://pay?${params.toString()}`;
  }, [senderData.money, senderData.name]);

  /* =========================================================
     ⭐ GENERATE QR CODE (as data URL)
     ⭐ KEPT SAME
  ========================================================= */

  useEffect(() => {
    if (paymentMethod !== "qr") return;

    let cancelled = false;

    const generateQr = async () => {
      try {
        setQrLoading(true);
        setQrError("");

        const upiString = buildUpiPaymentString();

        console.log("UPI STRING:", upiString);

        const dataUrl = await QRCode.toDataURL(upiString, {
          errorCorrectionLevel: "M",
          type: "image/png",
          quality: 0.95,
          margin: 2,
          width: 512,
          color: {
            dark: "#000000",
            light: "#ffffff",
          },
        });

        if (!cancelled) {
          setQrDataUrl(dataUrl);
        }
      } catch (err) {
        console.error("QR generation error:", err);
        if (!cancelled) {
          setQrError(
            "Unable to generate QR. Please try again."
          );
          setQrDataUrl("");
        }
      } finally {
        if (!cancelled) {
          setQrLoading(false);
        }
      }
    };

    generateQr();

    return () => {
      cancelled = true;
    };
  }, [paymentMethod, buildUpiPaymentString]);

  /* =========================================================
     HANDLERS
     ⭐ KEPT SAME
  ========================================================= */

  const handleGoHome = useCallback(() => {
    if (userId && uuid) {
      navigate(
        `/${userId}/${uuid}/partnerhome`,
        { replace: true }
      );
    } else {
      navigate("/partnerhome", {
        replace: true,
      });
    }
  }, [navigate, userId, uuid]);

  const handleGoToTasks = useCallback(() => {
    setShowMotivationPopup(false);

    if (userId && uuid) {
      navigate(
        `/${userId}/${uuid}/partnerhome`,
        { replace: true }
      );
    } else {
      navigate("/partnerhome", {
        replace: true,
      });
    }
  }, [navigate, userId, uuid]);

  /* =========================================================
     ⭐ CONFIRM PAYMENT — CLEARS SQL TABLE SENDER COLUMNS
     ⭐ KEPT SAME
  ========================================================= */

  const handlePaymentConfirm = useCallback(
    async () => {
      setLoading(true);

      try {
        /* Save payment method */
        try {
          localStorage.setItem(
            "selected_payment_method",
            paymentMethod
          );
        } catch {
          /* ignore */
        }

        /* ---------------------------------------------
           ⭐ CLEAR SENDER DETAILS IN DB
        --------------------------------------------- */

        if (userId || uuid) {
          try {
            const formData = new FormData();

            formData.append(
              "secondary_id",
              String(userId)
            );

            formData.append(
              "secondary_uuid",
              String(uuid)
            );

            console.log(
              "CLEAR SENDER → sending:",
              {
                secondary_id: userId,
                secondary_uuid: uuid,
              }
            );

            const response = await fetch(
              API_CLEAR_SENDER_URL,
              {
                method: "POST",
                body: formData,
              }
            );

            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}`
              );
            }

            const result =
              await response.json();

            console.log(
              "CLEAR SENDER RESULT:",
              result
            );

            if (!result?.success) {
              console.warn(
                "Clear sender failed:",
                result?.message
              );
            } else {
              /* ⭐ Instantly clear UI table */
              setSenderDetails(EMPTY_SENDER);
            }
          } catch (error) {
            console.error(
              "Clear sender error:",
              error
            );
            /* Continue even if clear fails */
          }
        } else {
          console.warn(
            "No userId / uuid in URL — skipping DB clear"
          );
        }

        /* ---------------------------------------------
           Clear localStorage sender data
        --------------------------------------------- */

        try {
          localStorage.removeItem(
            "selected_sender_name"
          );
          localStorage.removeItem(
            "selected_sender_number"
          );
          localStorage.removeItem(
            "selected_sender_address"
          );
          localStorage.removeItem(
            "selected_sender_lat_long"
          );
          localStorage.removeItem(
            "selected_sender_profile"
          );
          localStorage.removeItem(
            "selected_sender_money"
          );
          localStorage.removeItem(
            "selected_sender_chatbox"
          );
          localStorage.removeItem(
            "selected_sender_request"
          );
          localStorage.removeItem(
            "selected_sender_id"
          );
          localStorage.removeItem(
            "selected_sender_uuid"
          );
        } catch (error) {
          console.warn(
            "Local sender clear error:",
            error
          );
        }

        /* Small delay for smooth UX */
        await new Promise((r) =>
          setTimeout(r, 600)
        );

        /* Show Motivation Popup */
        setShowMotivationPopup(true);
      } catch (error) {
        console.error(
          "Payment confirm error:",
          error
        );

        alert(
          error?.message ||
            "Payment failed. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      paymentMethod,
      navigate,
      userId,
      uuid,
    ]
  );

  /* =========================================================
     ⭐ HELPER — get best money value to display
     Priority: partnerMoney (DB) → senderData.money → "00"
  ========================================================= */

  const displayMoney = useMemo(() => {
    if (
      partnerMoney !== null &&
      partnerMoney !== undefined &&
      String(partnerMoney).trim() !== ""
    ) {
      return partnerMoney;
    }
    if (senderData.money && senderData.money > 0) {
      return senderData.money;
    }
    return "00";
  }, [partnerMoney, senderData.money]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>

        <div className={styles.topGlow} />

        {/* TOP APP BAR */}
        <header className={styles.appBar}>
          <div className={styles.brandBlock}>
            <span className={styles.brandDot} />
            <span>BroBizee</span>
          </div>

          <button
            type="button"
            className={styles.homeBtn}
            onClick={handleGoHome}
            aria-label="Go to Home"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>
        </header>

        {/* SCROLL BODY */}
        <main className={styles.scrollContainer}>

          {/* Hero */}
          <section className={styles.heroSection}>
            <span className={styles.heroEyebrow}>
              SECURE PAYMENT
            </span>

            <h1 className={styles.heroTitle}>
              Complete Payment
            </h1>

            <p className={styles.heroSubtitle}>
              Choose your preferred payment method
              to continue with{" "}
              <strong>{senderData.name}</strong>
            </p>
          </section>

          {/* SENDER DETAILS TABLE */}
          <section className={styles.senderDetailsSection}>
            <div className={styles.methodHeader}>
              <span className={styles.methodEyebrow}>
                CUSTOMER DETAILS
              </span>

              <h3 className={styles.methodTitle}>
                Sender Information
              </h3>
            </div>

            {senderLoading ? (
              <div className={styles.senderDetailsLoading}>
                <span className={styles.btnSpinner} />
                <span>Loading sender details…</span>
              </div>
            ) : (
              <div className={styles.senderDetailsTable}>
                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Profile
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_profile_images ? (
                      <img
                        src={
                          senderDetails.sender_profile_images
                        }
                        alt="Sender"
                        className={styles.senderAvatar}
                      />
                    ) : (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Name
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_name || (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Number
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_number || (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Money
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_money !== "" &&
                    senderDetails.sender_money !== null ? (
                      <>₹ {senderDetails.sender_money}</>
                    ) : (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Address
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_address || (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Lat / Long
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_lat_long || (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Chatbox
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_chatbox || (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.senderDetailRow}>
                  <span className={styles.senderDetailLabel}>
                    Request
                  </span>
                  <span className={styles.senderDetailValue}>
                    {senderDetails.sender_request || (
                      <span className={styles.senderEmpty}>
                        —
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </section>

          {/* Amount Card */}
          <section className={styles.amountCard}>
            <div className={styles.amountLeft}>
              <span className={styles.amountLabel}>
                Amount to Pay
              </span>

              {/* ⭐ UPDATED — now shows DB money when back pressed */}
              <h2 className={styles.amountValue}>
                ₹ {displayMoney}
              </h2>
            </div>

            <div className={styles.amountIcon}
             onClick={handleClick}>
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect
                  x="2"
                  y="6"
                  width="20"
                  height="12"
                  rx="3"
                />
                <path d="M18 12h.01" />
              </svg>
            </div>
          </section>

          {/* PAYMENT METHODS */}
          <section className={styles.methodSection}>
            <div className={styles.methodHeader}>
              <span className={styles.methodEyebrow}>
                SELECT METHOD
              </span>

              <h3 className={styles.methodTitle}>
                Payment Options
              </h3>
            </div>

            <div className={styles.methodRow}>
              <button
                type="button"
                className={`${styles.methodBtn} ${
                  paymentMethod === "cash"
                    ? styles.methodBtnActive
                    : ""
                }`}
                onClick={() =>
                  setPaymentMethod("cash")
                }
              >
                <div className={styles.methodIconWrap}>
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="2"
                      y="6"
                      width="20"
                      height="12"
                      rx="2"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="2.5"
                    />
                    <path d="M6 12h.01" />
                    <path d="M18 12h.01" />
                  </svg>
                </div>

                <div className={styles.methodInfo}>
                  <strong>Cash</strong>
                  <span>Pay on delivery</span>
                </div>

                <div className={styles.methodCheck}>
                  {paymentMethod === "cash"
                    ? "✓"
                    : ""}
                </div>
              </button>

              <button
                type="button"
                className={`${styles.methodBtn} ${
                  paymentMethod === "qr"
                    ? styles.methodBtnActive
                    : ""
                }`}
                onClick={() =>
                  setPaymentMethod("qr")
                }
              >
                <div className={styles.methodIconWrap}>
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="7"
                      height="7"
                      rx="1"
                    />
                    <rect
                      x="14"
                      y="3"
                      width="7"
                      height="7"
                      rx="1"
                    />
                    <rect
                      x="3"
                      y="14"
                      width="7"
                      height="7"
                      rx="1"
                    />
                    <path d="M14 14h3v3h-3z" />
                    <path d="M21 14v3" />
                    <path d="M14 21h3" />
                    <path d="M21 21h.01" />
                  </svg>
                </div>

                <div className={styles.methodInfo}>
                  <strong>Scan QR</strong>
                  <span>UPI / Wallet</span>
                </div>

                <div className={styles.methodCheck}>
                  {paymentMethod === "qr"
                    ? "✓"
                    : ""}
                </div>
              </button>
            </div>
          </section>

          {/* ⭐ QR CODE PREVIEW — REAL SCANNABLE UPI QR */}
          {paymentMethod === "qr" && (
            <section className={styles.qrCard}>
              <div className={styles.qrCardHeader}>
                <span className={styles.qrEyebrow}>
                  SCAN TO PAY
                </span>

                <h3 className={styles.qrTitle}>
                  UPI Payment
                </h3>
              </div>

              <div className={styles.qrBox}>
                {qrLoading && (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <span
                      className={styles.btnSpinner}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        fontWeight: 600,
                      }}
                    >
                      Generating QR...
                    </span>
                  </div>
                )}

                {!qrLoading && qrError && (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: "8px",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <span
                      style={{ fontSize: "28px" }}
                    >
                      ⚠️
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#dc2626",
                        fontWeight: 600,
                      }}
                    >
                      {qrError}
                    </span>
                  </div>
                )}

                {!qrLoading && !qrError && qrDataUrl && (
                  <img
                    src={qrDataUrl}
                    alt="UPI Payment QR Code"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                )}
              </div>

              <p className={styles.qrHint}>
                Scan this QR with any UPI app
                {Number(displayMoney) > 0 && (
                  <>
                    {" "}
                    to pay{" "}
                    <strong
                      style={{ color: "#005CA9" }}
                    >
                      ₹ {displayMoney}
                    </strong>
                  </>
                )}
              </p>

              <div className={styles.qrUpiRow}>
                <span className={styles.qrUpiLabel}>
                  UPI ID
                </span>

                <span className={styles.qrUpiValue}>
                  {PARTNER_UPI_ID}
                </span>
              </div>

              {Number(displayMoney) > 0 && (
                <div
                  className={styles.qrUpiRow}
                  style={{ marginTop: "8px" }}
                >
                  <span className={styles.qrUpiLabel}>
                    Amount
                  </span>

                  <span
                    className={styles.qrUpiValue}
                    style={{
                      color: "#005CA9",
                      fontWeight: 800,
                    }}
                  >
                    ₹ {displayMoney}
                  </span>
                </div>
              )}

              {/* ⭐ DOWNLOAD BUTTON */}
              {qrDataUrl && (
                <a
                  href={qrDataUrl}
                  download={`BroBizee-UPI-QR-${
                    displayMoney || "payment"
                  }.png`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    marginTop: "12px",
                    padding: "10px 16px",
                    borderRadius: "10px",
                    background: "#f3f6ff",
                    border: "1px dashed #93b4ff",
                    color: "#005CA9",
                    fontSize: "12px",
                    fontWeight: 700,
                    textDecoration: "none",
                    width: "100%",
                    cursor: "pointer",
                    boxSizing: "border-box",
                  }}
                >
                  ⬇️ Download QR
                </a>
              )}
            </section>
          )}

          {/* TRUST BADGES */}
          <section className={styles.trustSection}>
            <div className={styles.trustItem}>
              <div className={styles.trustIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>

              <div>
                <strong>100% Secure</strong>
                <span>Encrypted payment</span>
              </div>
            </div>

            <div className={styles.trustItem}>
              <div className={styles.trustIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>

              <div>
                <strong>Verified</strong>
                <span>BroBizee Assured</span>
              </div>
            </div>

            <div className={styles.trustItem}>
              <div className={styles.trustIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                  />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>

              <div>
                <strong>Safe Policy</strong>
                <span>No hidden charges</span>
              </div>
            </div>
          </section>

          {/* CONFIRM BUTTON */}
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={handlePaymentConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className={styles.btnSpinner} />
                Processing...
              </>
            ) : (
              <>
                <span>
                  Confirm{" "}
                  {paymentMethod === "cash"
                    ? "Cash Payment"
                    : "QR Payment"}
                </span>

                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12H19" />
                  <path d="M13 6L19 12L13 18" />
                </svg>
              </>
            )}
          </button>

          {/* BOTTOM INFO */}
          <footer className={styles.bottomInfo}>
            <div className={styles.bottomInfoRow}>
              <span className={styles.bottomInfoDot} />
              <span>
                Secure payment powered by BroBizee
              </span>
            </div>

            <div className={styles.bottomInfoRow}>
              <span className={styles.bottomInfoDot} />
              <span>
                Your money is 100% protected under
                BroBizee Safe Policy
              </span>
            </div>

            <div className={styles.bottomInfoRow}>
              <span className={styles.bottomInfoDot} />
              <span>
                No refund after service completion
              </span>
            </div>

            <div className={styles.bottomBrand}>
              <span>Bro</span>
              <span className={styles.bottomBrandAccent}>
                Bizee
              </span>
              <b> • </b>
              <span className={styles.bottomBrandTag}>
                On Time. Online. On Demand.
              </span>
            </div>
          </footer>

          <div className={styles.bottomSpace} />
        </main>

        {/* MOTIVATION POPUP */}
        {showMotivationPopup && (
          <div
            className={styles.motivationOverlay}
            onClick={handleGoToTasks}
          >
            <div
              className={styles.motivationPopup}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.motivationTopStrip} />

              <span className={styles.motivationConfetti1} />
              <span className={styles.motivationConfetti2} />
              <span className={styles.motivationConfetti3} />

              <div className={styles.motivationIconWrap}>
                <span className={styles.motivationIconPulse} />
                <span className={styles.motivationIcon}>
                  🎉
                </span>
              </div>

              <div className={styles.motivationEyebrow}>
                PAYMENT SUCCESSFUL
              </div>

              <h2 className={styles.motivationTitle}>
                You're All Set, Partner!
              </h2>

              <p className={styles.motivationSubtitle}>
                Amazing work! Your payment is confirmed.
                You're now <strong>ready for the next task</strong>.
                Stay energized — new work is waiting for you.
              </p>

              <div className={styles.motivationBadges}>
                <div className={styles.motivationBadge}>
                  <span className={styles.motivationBadgeIcon}>
                    ⚡
                  </span>
                  <span>Ready</span>
                </div>

                <div className={styles.motivationBadge}>
                  <span className={styles.motivationBadgeIcon}>
                    💼
                  </span>
                  <span>New Work</span>
                </div>

                <div className={styles.motivationBadge}>
                  <span className={styles.motivationBadgeIcon}>
                    🚀
                  </span>
                  <span>Earn More</span>
                </div>
              </div>

              <button
                type="button"
                className={styles.motivationBtn}
                onClick={handleGoToTasks}
              >
                <span>Go to Tasks</span>

                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12H19" />
                  <path d="M13 6L19 12L13 18" />
                </svg>
              </button>

              <div className={styles.motivationFooter}>
                <span>💙</span>
                <span>
                  BroBizee — Partners helping Partners
                </span>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}